import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Box,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  MenuItem,
  TextField,
  Grid
} from '@mui/material'
import { NavigateNext as NavigateNextIcon } from '@mui/icons-material'
import IntegrationConfigForm from '../components/integrations/IntegrationConfigForm'
import integrationService from '../services/integrationService'
import type { IntegrationConfig, IntegrationConfigCreate, IntegrationConfigUpdate, ServiceType } from '../types/integration'

const SERVICE_TYPES: { value: ServiceType; label: string }[] = [
  { value: 'email', label: 'Email' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'logistics', label: 'Logistics' },
  { value: 'payment', label: 'Payment' }
]

const PROVIDERS_BY_SERVICE: Record<ServiceType, { value: string; label: string }[]> = {
  email: [
    { value: 'smtp', label: 'SMTP' }
  ],
  whatsapp: [
    { value: 'enotify_whatsapp', label: 'eNotify WhatsApp' }
  ],
  sms: [],
  logistics: [
    { value: 'delhivery_b2c', label: 'Delhivery B2C' },
    { value: 'delhivery_b2b', label: 'Delhivery B2B' }
  ],
  payment: [
    { value: 'razorpay', label: 'Razorpay' }
  ]
}

const IntegrationFormPage: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const isEditMode = !!id

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [integration, setIntegration] = useState<IntegrationConfig | null>(null)

  // For create mode
  const [selectedServiceType, setSelectedServiceType] = useState<ServiceType | ''>('')
  const [selectedProvider, setSelectedProvider] = useState<string>('')

  useEffect(() => {
    if (isEditMode && id) {
      loadIntegration(id)
    }
  }, [id, isEditMode])

  const loadIntegration = async (integrationId: string) => {
    try {
      setLoading(true)
      setError(null)
      const data = await integrationService.getIntegration(integrationId)
      setIntegration(data)
      setSelectedServiceType(data.service_type)
      setSelectedProvider(data.provider_name)
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load integration')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (data: IntegrationConfigCreate | IntegrationConfigUpdate) => {
    try {
      setError(null)

      if (isEditMode && id) {
        await integrationService.updateIntegration(id, data as IntegrationConfigUpdate)
      } else {
        await integrationService.createIntegration(data as IntegrationConfigCreate)
      }

      navigate('/admin/integrations')
    } catch (err: any) {
      setError(err.response?.data?.detail || `Failed to ${isEditMode ? 'update' : 'create'} integration`)
      throw err
    }
  }

  const handleCancel = () => {
    navigate('/admin/integrations')
  }

  const canShowForm = isEditMode ? !!integration : selectedServiceType && selectedProvider

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
        <Link
          color="inherit"
          href="#"
          onClick={(e) => {
            e.preventDefault()
            navigate('/admin/integrations')
          }}
        >
          Integrations
        </Link>
        <Typography color="text.primary">
          {isEditMode ? 'Edit Integration' : 'Create Integration'}
        </Typography>
      </Breadcrumbs>

      {/* Page Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          {isEditMode ? 'Edit Integration' : 'Create Integration'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {isEditMode
            ? 'Update the configuration for this integration'
            : 'Configure a new external service integration'}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Service Type and Provider Selection (Create Mode Only) */}
          {!isEditMode && (
            <Paper elevation={0} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Select Service Type and Provider
              </Typography>

              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    label="Service Type"
                    value={selectedServiceType}
                    onChange={(e) => {
                      setSelectedServiceType(e.target.value as ServiceType)
                      setSelectedProvider('') // Reset provider when service type changes
                    }}
                    required
                  >
                    <MenuItem value="">Select a service type</MenuItem>
                    {SERVICE_TYPES.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    label="Provider"
                    value={selectedProvider}
                    onChange={(e) => setSelectedProvider(e.target.value)}
                    disabled={!selectedServiceType}
                    required
                  >
                    <MenuItem value="">Select a provider</MenuItem>
                    {selectedServiceType &&
                      PROVIDERS_BY_SERVICE[selectedServiceType].map((provider) => (
                        <MenuItem key={provider.value} value={provider.value}>
                          {provider.label}
                        </MenuItem>
                      ))}
                  </TextField>
                </Grid>
              </Grid>
            </Paper>
          )}

          {/* Integration Configuration Form */}
          {canShowForm && (
            <IntegrationConfigForm
              serviceType={isEditMode ? integration!.service_type : (selectedServiceType as ServiceType)}
              providerName={isEditMode ? integration!.provider_name : selectedProvider}
              initialData={
                isEditMode && integration
                  ? {
                    is_active: integration.is_active,
                    is_primary: integration.is_primary,
                    priority: integration.priority,
                    config: integration.config || integration.config_data || {}
                  }
                  : undefined
              }
              integrationId={isEditMode && id ? id : undefined}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
            />
          )}
        </>
      )}
    </Box>
  )
}

export default IntegrationFormPage
